import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react'

export interface UserProfile {
  firstName: string
  lastName: string
  email: string
  timezone: string
  units: 'metric' | 'imperial'
  responseStyle: 'concise' | 'detailed' | 'tables-only'
  defaultRegion: string
  defaultModels: string[]
  athenaPresets: Record<string, boolean>
}

const DEFAULT_PROFILE: UserProfile = {
  firstName: 'Mathias',
  lastName: 'Vanderbrempt',
  email: 'mathiasvanderbrempt@gmail.com',
  timezone: 'Europe/Berlin',
  units: 'metric',
  responseStyle: 'concise',
  defaultRegion: 'central-europe',
  defaultModels: ['EPT-2', 'EC AIFS', 'EC IFS'],
  athenaPresets: {
    tables: true,
    deltas: true,
    divergence: true,
    wind: true,
    spreads: true,
    flows: false,
    balancing: false,
    confidence: true,
  },
}

interface UserProfileContextValue {
  profile: UserProfile
  updateProfile: (updates: Partial<UserProfile>) => void
}

const UserProfileContext = createContext<UserProfileContextValue | null>(null)

function loadProfile(): UserProfile {
  try {
    const stored = localStorage.getItem('jua-user-profile')
    if (stored) {
      return { ...DEFAULT_PROFILE, ...JSON.parse(stored) }
    }
  } catch (e) {
    console.error('Failed to load user profile', e)
  }
  return DEFAULT_PROFILE
}

export function UserProfileProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [profile, setProfile] = useState<UserProfile>(loadProfile)

  useEffect(() => {
    localStorage.setItem('jua-user-profile', JSON.stringify(profile))
  }, [profile])

  const updateProfile = useCallback((updates: Partial<UserProfile>) => {
    setProfile((prev) => ({ ...prev, ...updates }))
  }, [])

  return React.createElement(
    UserProfileContext.Provider,
    { value: { profile, updateProfile } },
    children,
  )
}

export function useUserProfile(): UserProfileContextValue {
  const ctx = useContext(UserProfileContext)
  if (!ctx)
    throw new Error('useUserProfile must be used within UserProfileProvider')
  return ctx
}
